import { apiRequestV2 } from "@/lib/queryClient";

const ALGORITHM = "RSA-OAEP-256+A256GCM" as const;

export type OuraEncryptedSecretPayload = {
  v: 1;
  alg: typeof ALGORITHM;
  keyId: string;
  encryptedKey: string;
  iv: string;
  ciphertext: string;
};

type OuraEncryptionKeyResponse = {
  keyId: string;
  algorithm: typeof ALGORITHM;
  publicKeySpkiBase64: string;
};

let publicKeyPromise: Promise<OuraEncryptionKeyResponse> | null = null;

function bytesToBase64(bytes: Uint8Array) {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function base64ToBytes(value: string) {
  return Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
}

async function getOuraEncryptionKey() {
  publicKeyPromise ??= apiRequestV2<OuraEncryptionKeyResponse>("GET", "/api/v1/oura/encryption-key");
  return publicKeyPromise;
}

export async function encryptOuraSecret(secret: string): Promise<OuraEncryptedSecretPayload> {
  const keyInfo = await getOuraEncryptionKey();
  const publicKey = await crypto.subtle.importKey(
    "spki",
    base64ToBytes(keyInfo.publicKeySpkiBase64),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  );
  const aesKey = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt"],
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    aesKey,
    new TextEncoder().encode(secret),
  );
  const rawAesKey = await crypto.subtle.exportKey("raw", aesKey);
  const encryptedKey = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    rawAesKey,
  );

  return {
    v: 1,
    alg: ALGORITHM,
    keyId: keyInfo.keyId,
    encryptedKey: bytesToBase64(new Uint8Array(encryptedKey)),
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
  };
}

export async function encryptOuraPayloadSecrets<TPayload>(payload: TPayload): Promise<TPayload> {
  if (!payload || typeof payload !== "object" || payload instanceof FormData) return payload;

  const mutable = { ...(payload as Record<string, unknown>) };
  for (const key of ["admin", "payer"]) {
    const value = mutable[key];
    if (typeof value === "string" && value.trim()) {
      mutable[key] = await encryptOuraSecret(value);
    }
  }

  return mutable as TPayload;
}
